/**
 * Side button geometries — cutout + insert + slot for each button.
 */
import * as THREE from 'three'
import {Brush, Evaluator, INTERSECTION, ADDITION} from 'three-bvh-csg'
import {mergeGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import {createRoundedRectShape} from '../../utils/geometry'
import {featureSegmentsForRect} from './quality'
import type {BodyConfig, SideButtonConfig} from '../types'

/** Depth of the side button cutout into the body */
export const SIDE_BUTTON_DEPTH = 1.0
/** Gap between button insert and cutout wall */
const SIDE_BUTTON_GAP = 0.05
/** Bevel radius for button insert edges */
const SIDE_BUTTON_BEVEL = 0.08

export interface SideButtonsResult {
  bodyCutters: THREE.BufferGeometry[]
  ffCutters: THREE.BufferGeometry[]
  insertGeos: THREE.BufferGeometry[]
  slotGeos: THREE.BufferGeometry[]
}

// Collect-only. The per-button slot INTERSECTION reads the raw (pre-cut) body —
// other buttons' cutouts are elsewhere, so the slot region is identical to the
// old growing-body result. Chamfer ADDITION stays internal; the combined cutout
// joins the global body/front-face batch subtracted once in the orchestrator.
export const buildSideButtons = (
  ev: Evaluator,
  mat: THREE.MeshStandardMaterial,
  body: BodyConfig,
  buttons: SideButtonConfig[],
  rawBodyGeo: THREE.BufferGeometry,
  rawFrontFaceGeo: THREE.BufferGeometry,
): SideButtonsResult => {
  if (buttons.length === 0) return {bodyCutters: [], ffCutters: [], insertGeos: [], slotGeos: []}
  const slotGeos: THREE.BufferGeometry[] = []
  const insertGeos: THREE.BufferGeometry[] = []
  const cutGeos: THREE.BufferGeometry[] = []

  for (const sb of buttons) {
    const isHorizontal = sb.side === 'left' || sb.side === 'right'
    let edgePos: number
    let signDir: number
    if (isHorizontal) {
      edgePos = sb.side === 'right' ? body.width / 2 : -(body.width / 2)
      signDir = sb.side === 'right' ? 1 : -1
    } else {
      edgePos = sb.side === 'top' ? body.height / 2 : -(body.height / 2)
      signDir = sb.side === 'top' ? 1 : -1
    }

    // Segment count for this button's rounded corners — small buttons drop to
    // the reduced tier. Reused for the cutout, slot, chamfer and insert (all the
    // same size class); see quality.ts.
    const btnSegments = featureSegmentsForRect(sb.width, sb.height, sb.cornerRadius)

    // Cutout
    const cutDepth = SIDE_BUTTON_DEPTH + 0.01
    const cutGeo = new THREE.ExtrudeGeometry(createRoundedRectShape(sb.width, sb.height, sb.cornerRadius), {
      depth: cutDepth,
      bevelEnabled: false,
      curveSegments: btnSegments,
    })
    cutGeo.translate(0, 0, -SIDE_BUTTON_DEPTH)
    if (isHorizontal) {
      cutGeo.rotateY((signDir * Math.PI) / 2)
      cutGeo.translate(edgePos, sb.position, 0)
    } else {
      cutGeo.rotateZ(Math.PI / 2)
      cutGeo.rotateX((-signDir * Math.PI) / 2)
      cutGeo.translate(sb.position, edgePos, 0)
    }

    // Slot cutout — slightly inset on perimeter and both ends so the slot mesh sits inside
    // the body's hole walls without coincident faces (avoids z-fighting on the cutout edges).
    // Outer end is also pushed inward to clear the chamfer lip and any recessed button face,
    // so the dark slot only shows below the visible chamfer/body and behind the recessed button.
    const slotInset = 0.005
    const extraInset = Math.max(0, -sb.depth, sb.chamfer)
    const slotCutDepth = SIDE_BUTTON_DEPTH - 2 * slotInset - extraInset
    let slotGeo: THREE.BufferGeometry = new THREE.BufferGeometry()
    if (slotCutDepth > 0.01) {
      const slotCutGeo = new THREE.ExtrudeGeometry(
        createRoundedRectShape(
          Math.max(0.01, sb.width - 2 * slotInset),
          Math.max(0.01, sb.height - 2 * slotInset),
          Math.max(0, sb.cornerRadius - slotInset),
        ),
        {depth: slotCutDepth, bevelEnabled: false, curveSegments: btnSegments},
      )
      slotCutGeo.translate(0, 0, -SIDE_BUTTON_DEPTH + slotInset)
      if (isHorizontal) {
        slotCutGeo.rotateY((signDir * Math.PI) / 2)
        slotCutGeo.translate(edgePos, sb.position, 0)
      } else {
        slotCutGeo.rotateZ(Math.PI / 2)
        slotCutGeo.rotateX((-signDir * Math.PI) / 2)
        slotCutGeo.translate(sb.position, edgePos, 0)
      }

      // Slot mesh — intersection of inset slotCut with body and front face
      const bodyIntBrush = new Brush(rawBodyGeo.clone(), mat)
      bodyIntBrush.updateMatrixWorld()
      const slotCutBrush1 = new Brush(slotCutGeo.clone(), mat)
      slotCutBrush1.updateMatrixWorld()
      const bodySlotGeo = ev.evaluate(bodyIntBrush, slotCutBrush1, INTERSECTION).geometry

      const ffIntBrush = new Brush(rawFrontFaceGeo.clone(), mat)
      ffIntBrush.updateMatrixWorld()
      const slotCutBrush2 = new Brush(slotCutGeo.clone(), mat)
      slotCutBrush2.updateMatrixWorld()
      const ffSlotGeo = ev.evaluate(ffIntBrush, slotCutBrush2, INTERSECTION).geometry

      const hasVerts = (g: THREE.BufferGeometry) => {
        const pos = g.getAttribute('position')
        return pos && pos.count > 0
      }
      const parts = [bodySlotGeo, ffSlotGeo].filter(hasVerts)
      slotGeo = parts.length > 0 ? (mergeGeometries(parts) ?? new THREE.BufferGeometry()) : new THREE.BufferGeometry()
      slotGeo.computeVertexNormals()
    }
    slotGeos.push(slotGeo)

    // Optional chamfer cutout — beveled lip at the cutout entrance
    // Built so the bottom bevel transitions from FULL (at z = -chamfer, deeper in body)
    // to FULL + 2*chamfer (at z = 0, body surface). The portion above z=0 is outside body.
    let chamferGeo: THREE.BufferGeometry | null = null
    if (sb.chamfer > 0) {
      chamferGeo = new THREE.ExtrudeGeometry(
        createRoundedRectShape(sb.width + 2 * sb.chamfer, sb.height + 2 * sb.chamfer, sb.cornerRadius + sb.chamfer),
        {
          depth: 0.001,
          bevelEnabled: true,
          bevelThickness: sb.chamfer,
          bevelSize: sb.chamfer,
          bevelSegments: 16,
          curveSegments: btnSegments,
        },
      )
      if (isHorizontal) {
        chamferGeo.rotateY((signDir * Math.PI) / 2)
        chamferGeo.translate(edgePos, sb.position, 0)
      } else {
        chamferGeo.rotateZ(Math.PI / 2)
        chamferGeo.rotateX((-signDir * Math.PI) / 2)
        chamferGeo.translate(sb.position, edgePos, 0)
      }
    }

    // Combine main cutout with chamfer (if any) so the body subtraction does both at once
    let combinedCutGeo: THREE.BufferGeometry = cutGeo
    if (chamferGeo) {
      const baseCutBrush = new Brush(cutGeo, mat)
      baseCutBrush.updateMatrixWorld()
      const chamferBrush = new Brush(chamferGeo, mat)
      chamferBrush.updateMatrixWorld()
      combinedCutGeo = ev.evaluate(baseCutBrush, chamferBrush, ADDITION).geometry
    }

    // Defer the body/front-face subtraction — batched after the loop.
    cutGeos.push(combinedCutGeo)

    // Insert — anchored at the cutout's far wall, outer face at edgePos + signDir * sb.depth
    const buttonLength = SIDE_BUTTON_DEPTH + sb.depth
    const insW = sb.width - 2 * SIDE_BUTTON_GAP - 2 * SIDE_BUTTON_BEVEL
    const insH = sb.height - 2 * SIDE_BUTTON_GAP - 2 * SIDE_BUTTON_BEVEL
    const insR = Math.max(0, sb.cornerRadius - SIDE_BUTTON_GAP - SIDE_BUTTON_BEVEL)
    const insExtrudeDepth = Math.max(0.01, buttonLength - 2 * SIDE_BUTTON_BEVEL)
    const insGeo = new THREE.ExtrudeGeometry(createRoundedRectShape(insW, insH, insR), {
      depth: insExtrudeDepth,
      bevelEnabled: true,
      bevelThickness: SIDE_BUTTON_BEVEL,
      bevelSize: SIDE_BUTTON_BEVEL,
      bevelSegments: 4,
      curveSegments: btnSegments,
    })
    insGeo.translate(0, 0, -(insExtrudeDepth / 2))
    const centerOffset = (sb.depth - SIDE_BUTTON_DEPTH) / 2
    if (isHorizontal) {
      insGeo.rotateY((signDir * Math.PI) / 2)
      insGeo.translate(edgePos + signDir * centerOffset, sb.position, 0)
    } else {
      insGeo.rotateZ(Math.PI / 2)
      insGeo.rotateX((-signDir * Math.PI) / 2)
      insGeo.translate(sb.position, edgePos + signDir * centerOffset, 0)
    }
    insGeo.computeVertexNormals()
    insertGeos.push(insGeo)
  }

  // Each combined (cutout + chamfer) shape cuts both the body and the front face.
  return {bodyCutters: cutGeos, ffCutters: cutGeos, insertGeos, slotGeos}
}
